const {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
  createContext,
} = require('react');

const fields = { default: {} };
const fieldValues = { default: {} };

const transformToErrorFields = (errors) => {
  const errorFields = {};
  Object.keys(errors).map((name) => {
    errorFields[`${name}-error`] = errors[name];
  });

  return errorFields;
};

const onFieldChange = (context, name, cb) => {
  fields[context][name] = fields[context][name] || cb;
};

const dispatchFieldValueChange = (context, name, value) => {
  Object.keys(fields[context]).map((key) => {
    if (key === name && fields[context][key]) {
      fields[context][key](value);
    }
  });
};

const setFieldsValue = (context, values) =>
  Object.keys(values).map((name) => {
    dispatchFieldValueChange(context, name, values[name]);
    fieldValues[context][name] = values[name];
  });

const setFieldsError = (context, errors) =>
  setFieldsValue(context, transformToErrorFields(errors));

const resetFieldsError = (context, errorNames) => {
  const errors = {};
  errorNames.map((name) => {
    errors[name] = null;
  });

  setFieldsError(context, errors);
};

const getFieldsValue = (context) => fieldValues[context];

const getWatchedFieldValues = (context, fileds) => {
  const updatedValues = {};
  fileds.map((name) => (updatedValues[name] = fieldValues[context][name]));

  return updatedValues;
};

const useWatchFields = (context, fields) => {
  const [values, setValues] = useState(() =>
    getWatchedFieldValues(context, fields),
  );

  useEffect(() => {
    fields.map((name) =>
      onFieldChange(context, name, (value) => {
        setValues((oldValues) => ({ ...oldValues, [name]: value }));
      }),
    );
  }, []);

  return values;
};

const FieldContext = createContext('default');

const useField = ({ values } = {}) => {
  const context = Math.random();
  fields[context] = {};
  fieldValues[context] = values || {};

  return {
    context,
  };
};

const useFieldContext = () => {
  const context = useContext(FieldContext);

  return {
    onFieldChange: (name, cb) => onFieldChange(context, name, cb),
    setFieldsValue: (inputs) => setFieldsValue(context, inputs),
    getFieldsValue: () => getFieldsValue(context),
    useWatchFields: (fields) => useWatchFields(context, fields),
    setFieldsError: (errors) => setFieldsError(context, errors),
    resetFieldsError: (errorNames) => resetFieldsError(context, errorNames),
    context,
  };
};

const Field = ({ children, name, value: initialValue }) => {
  const { getFieldsValue, onFieldChange, context } = useFieldContext();
  const [value, setValue] = useState(initialValue || getFieldsValue()[name]);
  const [error, setError] = useState(undefined);

  const onChangeValue = useCallback(
    (value) => {
      fieldValues[context][name] = value;
      setValue(value);
    },
    [name],
  );

  const setErrorValue = useCallback(
    (error) => {
      fieldValues[context][`${name}-error`] = error;
      setError(error);
    },
    [name],
  );

  const field = useMemo(() => {
    const onChange = (e) => {
      onChangeValue(e.target ? e.target.value : e);
    };
    return { name, onChange };
  }, [name]);

  useEffect(() => {
    onFieldChange(name, (value) => onChangeValue(value));
    onFieldChange(`${name}-error`, (error) => setErrorValue(error));
  }, []);

  return children({ name, value, field, error });
};

module.exports = {
  Field,
  FieldContext,

  useField,
  useFieldContext,

  setFieldsValue,
  getFieldsValue,
  useWatchFields,
};
